import {
  useAuthsvsPasskeys,
  useGetEnrollmentJointWelcomeData,
  useSubmitEnrollmentJointWelcomeData,
} from '@egw/api'
import { useAuth } from '@egw/auth'
import { Button, GenericError } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { WelcomeStepsJointV1 } from '../../../components/welcome/welcome-steps-joint-v1'
import { WelcomeStepsJointV2 } from '../../../components/welcome/welcome-steps-joint-v2'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { ROUTES as ROUTES_JOINT } from '../../../constants/routes/routes-joint'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Joint - Welcome')

export const WelcomePage: FC<{ variant: 'one-accepted-disclosures' | 'both-accepted-disclosures' }> = ({ variant }) => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const { isAuthenticated, refreshToken } = useAuth()

  // Refresh token on mount as user should be authenticated
  // This will ensure that if the user will reach Overview page
  useEffect(() => {
    void refreshToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, isLoading, error } = useGetEnrollmentJointWelcomeData()
  // Attempt to fetch passkeys if user is authenticated
  // Retry is false to prevent infinite loading state
  const {
    data: authsvsPasskeyData,
    isLoading: isGettingPasskeys,
    error: passkeyError,
  } = useAuthsvsPasskeys({ enabled: isAuthenticated, retry: false })

  const hasPasskeys = Array.isArray(authsvsPasskeyData) && authsvsPasskeyData.length > 0

  const { isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointWelcomeData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint welcome data', error)
    }
  }, [error])

  // Log passkey fetching errors
  useEffect(() => {
    if (passkeyError) {
      log.error('Error fetching passkeys', passkeyError)
    }
  }, [passkeyError])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint welcome form', submitError)
    }
  }, [submitError])

  // Extract data from API response
  const activePerson = data?.activePerson
  const jointPerson = data?.jointPerson
  const applicationId = data && !data.isError ? data.activePerson?.applicationId : ''

  // Protect route
  useValidateRoute(activePerson?.path)

  // When both Persons have accepted disclosures
  const onSubmit = async (): Promise<void> => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_CONTINUE_CLICKED, { applicationId })
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_SUBMIT, { applicationId })

    if (hasPasskeys) {
      void navigate(WEALTH_ROUTES.OVERVIEW.path)
    } else {
      void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES_JOINT.CREATE_PASSKEY.path}`)
    }
  }

  // When other Person has not accepted disclosures
  const onSubmitV2 = (): void => {
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_CONTINUE_CLICKED, { applicationId })
    sendEvent(EDataLayerEventWealth.BANKHYCHYS_WELCOME_SUBMIT, { applicationId })
    window.location.href = 'https://www.evergreenwealth.com/'
  }

  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentJointLayout title={t('joint.welcome.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <>
          <div>
            <div className="flex flex-col sm:flex-row mb-lg sm:gap-xs gap-2xs">
              <h1 className="typo-primary-4xl-light text-text-header">
                {t('joint.welcome.greeting')}{' '}
                {variant === 'both-accepted-disclosures' ? (
                  <>
                    <span className="typo-primary-4xl-semibold text-text-header">
                      {activePerson?.name?.firstName ?? ''}
                    </span>{' '}
                    and{' '}
                    <span className="typo-primary-4xl-semibold text-text-header">
                      {jointPerson?.name?.firstName ?? ''}
                    </span>
                    ,
                  </>
                ) : (
                  <span className="typo-primary-4xl-semibold text-text-header">
                    {activePerson?.name?.firstName ?? ''},
                  </span>
                )}
              </h1>
            </div>
            <p className="mb-xl typo-primary-xl-regular text-text-body">{t('joint.welcome.just_starting')}</p>
            <div className="flex flex-col">
              <h3 className="typo-primary-lg-light text-text-header mb-md">{t('joint.welcome.whats_next')}</h3>
              {variant === 'both-accepted-disclosures' ? (
                <WelcomeStepsJointV1 jointPersonFirstName={jointPerson?.name?.firstName ?? ''} />
              ) : (
                <WelcomeStepsJointV2 jointPersonFirstName={jointPerson?.name?.firstName ?? ''} />
              )}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isDisabled={isSubmitting || isGettingPasskeys}
            isLoading={isSubmitting}
            testId="primaryAction_button"
            onClick={() => {
              if (variant === 'both-accepted-disclosures') {
                void onSubmit()
              } else {
                onSubmitV2()
              }
            }}
          >
            {variant === 'both-accepted-disclosures' ? t('common.continue') : t('joint.welcome.continue_v2')}
          </Button>
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
